import {getTranslations, isWorker} from '@wix/wixstores-client-storefront-sdk/dist/src/viewer-script/utils';
import {IHostProps} from '@wix/native-components-infra/dist/src/types/types';
import {SiteCurrency} from '@wix/ambassador-currency-site-settings/types';
import {CurrencySymbols, CurrencySymbolsMap} from '../../settings/services/currency.utility';
import {CurrencyService} from '../../services/CurrencyService';
import {APP_DEFINITION_ID, WIDGET_ID} from '../constants';
import {CurrencyConverterSiteStore} from './currencyConverterSiteStore';
import {GUID} from '@wix/bi-logger-ec-sf/v2/types';
import {currencyConverterChooseSiteCurrencyDisplay} from '@wix/bi-logger-ec-sf/v2';
import {TranslationMap, ErrorReporter, BiParams} from '../../types/common';
import {ExperimentsBag} from '@wix/wix-experiments';
import {IHttpClient} from '@wix/http-client';
import {getPayableCurrencies} from '@wix/ambassador-cashier-pay-v2-account/http';
import {LogObject} from '@wix/bi-logger-payments-data/v2/types';

export interface IPropsInjectedByViewerScript {
  currencySymbols: CurrencySymbols;
  userCurrencies: string[];
  selectedCurrency: string;
  onCurrencySelected: (code: string) => void;
  onAppLoaded: () => void;
  reportAppNotLoaded: CurrencyConverterStore['reportAppNotLoaded'];
  translations: TranslationMap;
  isRtl: boolean;
  isMobile: boolean;
  host: IHostProps;
  ravenUserContextOverrides: {id: string; uuid: GUID};
  experiments: ExperimentsBag;
  suggestedCurrencyCode: string;
  biParams: BiParams;
  onBIEvent: (event: LogObject<unknown>) => void;
}

export class CurrencyConverterStore {
  private readonly updateComponent: (props: Partial<IPropsInjectedByViewerScript>) => void;
  private readonly currencyService: CurrencyService;
  private currencySymbols: CurrencySymbols;
  private userCurrencies: string[];
  private selectedCurrency: string;
  private readonly siteStore: CurrencyConverterSiteStore;
  private readonly fedopsLogger;
  private shouldReportFedops: boolean;
  private readonly reportError: ErrorReporter;
  private readonly experiments: ExperimentsBag;
  private readonly httpClient: IHttpClient;
  private suggestedCurrencyCode: string;
  private readonly visitorId: string;

  constructor({
    siteStore,
    currencyService,
    updateComponent,
    reportError,
    experiments,
    httpClient,
    visitorId,
  }: {
    siteStore: CurrencyConverterSiteStore;
    currencyService: CurrencyService;
    updateComponent: (props: Partial<IPropsInjectedByViewerScript>) => void;
    reportError: ErrorReporter;
    experiments: ExperimentsBag;
    httpClient: IHttpClient;
    visitorId: string;
  }) {
    const fedopsLoggerFactory = siteStore.platformServices.fedOpsLoggerFactory;
    this.reportError = reportError;
    this.fedopsLogger = fedopsLoggerFactory.getLoggerForWidget({
      appId: APP_DEFINITION_ID,
      widgetId: WIDGET_ID,
    });

    if (isWorker()) {
      this.shouldReportFedops = true;
      this.fedopsLogger.appLoadStarted();
    }

    this.updateComponent = updateComponent;
    this.currencyService = currencyService;
    this.siteStore = siteStore;
    this.experiments = experiments;
    this.httpClient = httpClient;
    this.visitorId = visitorId;

    this.siteStore.location.onChange(() => {
      if (this.selectedCurrency !== this.siteStore.location.query.currency) {
        this.updateComponent({
          selectedCurrency: this.siteStore.location.query.currency || this.siteStore.siteMainCurrency,
        });
      }
    });
  }

  private readonly onCurrencySelected = (currency: string) => {
    if (currency === this.siteStore.siteMainCurrency) {
      this.siteStore.removeQueryParams(['currency']);
    } else {
      this.siteStore.updateQueryParams({currency});
    }
    this.siteStore.webBiLogger.report(
      currencyConverterChooseSiteCurrencyDisplay({
        newValue: currency,
        siteCurrency: this.siteStore.siteMainCurrency,
      })
    );
  };

  private readonly onBIEvent = (event: LogObject<unknown>) => {
    this.siteStore.webBiLogger.report(event);
  };

  private getUserCurrenciesWithMainSiteCurrencyFirst(mainCurrency: string, additionalCurrencies: SiteCurrency[]) {
    return [mainCurrency, ...additionalCurrencies.map(({code}) => code).filter((code) => code !== mainCurrency)];
  }

  private async getTranslations(locale: string, fallbackLocale: string) {
    let translations;
    try {
      translations = await getTranslations(
        `${this.siteStore.baseUrls.currencyConverterBaseUrl}assets/locales/widget/messages_${locale}.json`
      );
    } catch (ex) {
      translations = await getTranslations(
        `${this.siteStore.baseUrls.currencyConverterBaseUrl}assets/locales/widget/messages_${fallbackLocale}.json`
      );
    }

    return translations;
  }

  public async setInitialState(reportError: ErrorReporter): Promise<void> {
    this.selectedCurrency = this.siteStore.currentCurrencyCode;
    this.userCurrencies = this.getUserCurrenciesWithMainSiteCurrencyFirst(this.siteStore.siteMainCurrency, [
      {code: this.selectedCurrency},
    ]);

    try {
      const [translations] = await Promise.all([
        this.getTranslations(this.siteStore.locale, 'en'),
        this.loadInitialData(reportError),
      ]);

      this.updateComponent({
        ravenUserContextOverrides: {
          id: this.siteStore.storeId,
          uuid: this.siteStore.uuid,
        },
        userCurrencies: this.userCurrencies,
        currencySymbols: this.currencySymbols,
        selectedCurrency: this.selectedCurrency,
        onAppLoaded: this.onAppLoaded,
        onCurrencySelected: this.onCurrencySelected,
        translations,
        isRtl: this.siteStore.isRTL(),
        isMobile: this.siteStore.isMobile(),
        experiments: this.experiments,
        suggestedCurrencyCode: this.suggestedCurrencyCode,
        biParams: {
          msid: this.siteStore.msid,
          instance: this.siteStore.instance,
          currentPageId: this.siteStore.siteApis.currentPage.id,
          currentPageName: this.siteStore.siteApis.currentPage.name,
          ownerId: this.siteStore.ownerId,
          visitorId: this.visitorId,
        },
        onBIEvent: this.onBIEvent,
      });

      /* istanbul ignore next: need test - ssr  */
      if (this.siteStore.isSSR()) {
        this.onAppLoaded();
      }
    } catch (error) {
      reportError(error);
    }
  }

  public onAppLoaded = (): void => {
    if (this.shouldReportFedops) {
      this.fedopsLogger.appLoaded();
      this.shouldReportFedops = false;
    }
  };

  public reportAppNotLoaded = (selectedCurrency: string, onAppLoaded): void => {
    const message = `selectedCurrency = ${selectedCurrency}, type onAppLoaded === 'function' = ${
      typeof onAppLoaded === 'function'
    }`;
    this.reportError(new Error(message));
  };

  public async loadInitialData(reportError: ErrorReporter): Promise<void> {
    try {
      const [allCurrencies, userCurrencies, payableCurrencies] = await Promise.all([
        this.currencyService.getAvailableCurrenciesList(),
        this.currencyService.getCurrencies(),
        this.getPayableCurrenciesList(),
      ]);

      this.currencySymbols = CurrencySymbolsMap(allCurrencies);

      this.selectedCurrency = this.siteStore.currentCurrencyCode;
      this.userCurrencies = this.getUserCurrenciesWithMainSiteCurrencyFirst(
        this.siteStore.siteMainCurrency,
        userCurrencies
      );
      this.suggestedCurrencyCode = payableCurrencies.suggestedCurrencyCode;
      const selectedCurrency = this.userCurrencies.find((code) => code === this.selectedCurrency);
      if (!selectedCurrency) {
        this.userCurrencies.push(this.selectedCurrency);
      }

      this.updateComponent({
        userCurrencies: this.userCurrencies,
        currencySymbols: this.currencySymbols,
        suggestedCurrencyCode: this.suggestedCurrencyCode,
      });
    } catch (e) {
      reportError(e);
      throw e;
    }
  }

  public async getPayableCurrenciesList() {
    try {
      const {data} = await this.httpClient.request(getPayableCurrencies({}));
      return data;
    } catch (e) {
      return {currencyCode: [], suggestedCurrencyCode: ''};
    }
  }
}
